import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Registration } from '../models/registration.model';
import { CancellationPreview } from '../models/cancellation.model';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  private readonly apiUrl = `${environment.apiUrl}/registrations`;
  private myRegistrationsSubject = new BehaviorSubject<Registration[]>([]);
  public myRegistrations$ = this.myRegistrationsSubject.asObservable();

  private authService = inject(AuthService);

  constructor(private http: HttpClient) {
    this.authService.currentUser$.subscribe(user => {
      if (user) {
        this.loadMyRegistrations();
      } else {
        this.myRegistrationsSubject.next([]);
      }
    });
  }

  public loadMyRegistrations() {
    this.http.get<Registration[]>(this.apiUrl).pipe(
      catchError(() => of([]))
    ).subscribe(regs => {
      const unique = this.dedupeById(regs);
      this.myRegistrationsSubject.next(unique);
    });
  }

  subscribe(registration: Registration): Observable<Registration> {
    return this.http.post<Registration>(this.apiUrl, registration).pipe(
      tap(() => this.loadMyRegistrations())
    );
  }

  getCancellationPreview(registrationId: number): Observable<CancellationPreview> {
    return this.http.get<CancellationPreview>(`${this.apiUrl}/${registrationId}/cancellation`);
  }

  /** Gratuita/não paga: cancela na hora. Paga: abre pedido para o organizador. */
  requestCancellation(registrationId: number, reason: string | null): Observable<CancellationPreview> {
    return this.http.post<CancellationPreview>(
      `${this.apiUrl}/${registrationId}/cancellation`, { message: reason }
    ).pipe(tap(() => this.loadMyRegistrations()));
  }

  private dedupeById(regs: Registration[]): Registration[] {
    const seen = new Set<number>();
    return regs.filter(reg => {
      if (reg.id == null) return true;
      if (seen.has(reg.id)) return false;
      seen.add(reg.id);
      return true;
    });
  }

  isSubscribed(eventId: number): boolean {
    return this.myRegistrationsSubject.value.some(
      r => r.event?.id === eventId && r.status === 'CONFIRMED'
    );
  }

  getPendingRegistration(eventId: number): Registration | undefined {
    return this.myRegistrationsSubject.value.find(
      r => r.event?.id === eventId && r.status === 'PENDING'
    );
  }
}
